import { takeEvery, all, put, call, cancelled, takeLatest } from 'redux-saga/effects'
import { PayloadAction } from '@reduxjs/toolkit'
import { ApiError, RequestStatus } from 'features/common'
import { appsRequest, featuredAppsRequest, appRequest, saveAppRequest, deleteAppRequest } from './appsSlice'
import {
  addApps,
  addFeaturedApps,
  setCurrentApp,
  setMyAppsStatus,
  setMyAppsError,
  setFeaturedAppsStatus,
  setFeaturedAppsError,
  setSaveAppStatus,
  setSaveAppError,
  setDeleteAppStatus,
  setDeleteAppError,
  trackAppSelectedRequest,
  bookmarkedAppsRequest,
  setBookmarkedApps,
  setBookmarkedAppsStatus,
  setBookmarkedAppsError
} from './appsSlice'
import {
  getApps,
  getApp,
  saveApp as saveAppApiRequest,
  deleteApp as deleteAppApiRequest,
  trackAppSelected,
  getFeaturedApps,
  getBookmarkedApps
} from '../api/apps'
import { setStatsStatus } from 'features/account/state/accountSlice'
import { App, AppTypes, FeaturedApp } from '../types'
import { User } from 'features/users'
import { createApiError } from 'features/common/api/apiError'

function* fetchAppsData() {
  const controller = new AbortController()
  const { signal } = controller
  try {
    yield put(setMyAppsStatus(RequestStatus.Pending))
    const appsData: Array<App> = yield call(getApps, { signal })
    yield put(addApps(appsData))
    yield put(setMyAppsStatus(RequestStatus.Fulfilled))
  } catch (e) {
    yield put(setMyAppsStatus(RequestStatus.Rejected))
    yield put(setMyAppsError(createApiError(e)))
  } finally {
    if (yield cancelled()) {
      controller.abort()
    }
  }
}

function* fetchBookmarkedApps() {
  try {
    yield put(setBookmarkedAppsStatus(RequestStatus.Pending))
    const bookmarkedApps: Array<App> = yield call(getBookmarkedApps)
    yield put(setBookmarkedApps(bookmarkedApps))
    yield put(setBookmarkedAppsStatus(RequestStatus.Fulfilled))
  } catch (e) {
    yield put(setBookmarkedAppsStatus(RequestStatus.Rejected))
    yield put(setBookmarkedAppsError(e as ApiError))
  }
}

function* fetchApp(action: PayloadAction<string>) {
  try {
    yield put(setMyAppsStatus(RequestStatus.Pending))
    const app: App = yield call(getApp, action.payload)
    yield put(setCurrentApp(app))
    yield put(setMyAppsStatus(RequestStatus.Fulfilled))
  } catch (e) {
    yield put(setMyAppsStatus(RequestStatus.Rejected))
    yield put(setMyAppsError(createApiError(e)))
  }
}

function* saveApp(action: PayloadAction<App>) {
  try {
    yield put(setSaveAppStatus(RequestStatus.Pending))
    yield call(saveAppApiRequest, action.payload)
    yield put(setSaveAppStatus(RequestStatus.Fulfilled))
    yield put(setSaveAppStatus(RequestStatus.Idle))
    yield put(setMyAppsStatus(RequestStatus.Idle))
    yield put(setStatsStatus(RequestStatus.Idle))
  } catch (e) {
    yield put(setSaveAppError(e as ApiError))
    yield put(setSaveAppStatus(RequestStatus.Rejected))
  }
}

function* deleteApp(action: PayloadAction<{ id: string; type: AppTypes }>) {
  try {
    yield put(setDeleteAppStatus(RequestStatus.Pending))
    yield call(deleteAppApiRequest, action.payload)
    yield put(setDeleteAppStatus(RequestStatus.Fulfilled))
    yield put(setDeleteAppStatus(RequestStatus.Idle))
    yield put(setMyAppsStatus(RequestStatus.Idle))
    yield put(setStatsStatus(RequestStatus.Idle))
  } catch (e) {
    yield put(setDeleteAppStatus(RequestStatus.Rejected))
    yield put(setDeleteAppError(e as ApiError))
  }
}

function* trackAppSelectedMetric(action: PayloadAction<{ user: User; appId: string; type: string }>) {
  try {
    yield call(trackAppSelected, action.payload)
  } catch (e) {
    // TODO
  }
}

function* fetchFeaturedAppsData() {
  const controller = new AbortController()
  try {
    yield put(setFeaturedAppsStatus(RequestStatus.Pending))
    const featuredAppsData: Array<FeaturedApp> = yield call(getFeaturedApps)
    yield put(addFeaturedApps(featuredAppsData))
    yield put(setFeaturedAppsStatus(RequestStatus.Fulfilled))
  } catch (e) {
    yield put(setFeaturedAppsStatus(RequestStatus.Rejected))
    yield put(setFeaturedAppsError(e as ApiError))
  } finally {
    if (yield cancelled()) {
      controller.abort()
    }
  }
}

export function* watchApps() {
  yield takeLatest(appsRequest.type, fetchAppsData)
  yield takeLatest(featuredAppsRequest.type, fetchFeaturedAppsData)
  yield takeEvery(appRequest.type, fetchApp)
  yield takeEvery(saveAppRequest.type, saveApp)
  yield takeEvery(deleteAppRequest.type, deleteApp)
  yield takeEvery(trackAppSelectedRequest.type, trackAppSelectedMetric)
  yield takeLatest(bookmarkedAppsRequest.type, fetchBookmarkedApps)
}

export default function* appsSaga() {
  try {
    yield all([watchApps()])
  } catch (e) {
    // TODO
  }
}
